import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Building,
  Users,
  CreditCard,
  TrendingUp,
  AlertTriangle,
  Calendar,
  DollarSign,
  Zap
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { reportsAPI } from '../services/api';
import { formatCurrency, getMonthName } from '../utils/helpers';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('6'); // For electricity chart
  const [selectedConsumptionMonth, setSelectedConsumptionMonth] = useState('this-month'); // For consumption panel
  const [selectedRooms, setSelectedRooms] = useState('all'); // For electricity chart room filter
  const [chartViewType, setChartViewType] = useState('stacked'); // stacked or grouped

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Re-fetch data when consumption month changes
  useEffect(() => {
    if (stats) { // Only fetch if we have initial data loaded
      fetchDashboardData();
    }
  }, [selectedConsumptionMonth]);

  const fetchDashboardData = async () => {
    try {
      const params = {};
      
      // Add consumption month/year parameters if not "this-month"
      if (selectedConsumptionMonth !== 'this-month') {
        const [year, month] = selectedConsumptionMonth.split('-');
        params.consumption_month = parseInt(month);
        params.consumption_year = parseInt(year);
      }
      
      const response = await reportsAPI.getDashboard(params);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Generate last 12 months for consumption dropdown
  const generateMonthOptions = () => {
    const months = [];
    const today = new Date();
    
    // Add "This Month" option
    months.push({
      value: 'this-month',
      label: 'This Month',
      month: today.getMonth() + 1,
      year: today.getFullYear()
    });
    
    // Add last 11 months
    for (let i = 1; i <= 11; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      months.push({
        value: `${date.getFullYear()}-${date.getMonth() + 1}`,
        label: monthName,
        month: date.getMonth() + 1,
        year: date.getFullYear()
      });
    }
    
    return months;
  };

    const monthOptions = generateMonthOptions();

  // Get selected month display name
  const getSelectedMonthLabel = () => {
    const selectedOption = monthOptions.find(option => option.value === selectedConsumptionMonth);
    return selectedOption ? selectedOption.label.toLowerCase() : 'current month';
  };

  // Get color for consumption bar based on percentage
  const getConsumptionBarColor = (percentage) => {
    if (percentage >= 80) return 'bg-gradient-to-r from-red-500 to-red-600'; // High consumption - Red
    if (percentage >= 60) return 'bg-gradient-to-r from-orange-500 to-red-500'; // Medium-high - Orange to Red
    if (percentage >= 40) return 'bg-gradient-to-r from-yellow-500 to-orange-500'; // Medium - Yellow to Orange
    if (percentage >= 20) return 'bg-gradient-to-r from-green-400 to-yellow-500'; // Low-medium - Green to Yellow
    return 'bg-gradient-to-r from-green-500 to-green-400'; // Low consumption - Green
  };

  // Stats cards data
  const statsCards = [
    {
      title: 'Total Rooms',
      value: stats?.rooms?.total_rooms || 0,
      subtitle: `${stats?.rooms?.occupied_rooms || 0} occupied`,
      icon: Building,
      color: 'primary',
      trend: null
    },
    {
      title: 'Total Tenants',
      value: stats?.tenants?.total_tenants || 0,
      subtitle: 'Active tenants',
      icon: Users,
      color: 'info',
      trend: null
    },
    {
      title: 'Monthly Rent',
      value: formatCurrency(stats?.rent?.total_rent_collected || 0),
      subtitle: `of ${formatCurrency(stats?.rent?.total_rent_due || 0)} due`,
      icon: DollarSign,
      color: 'success',
      trend: stats?.rent ? ((stats.rent.total_rent_collected / stats.rent.total_rent_due) * 100).toFixed(1) : 0
    },
    {
      title: 'Electricity',
      value: formatCurrency(stats?.electricity?.total_electricity_collected || 0),
      subtitle: `of ${formatCurrency(stats?.electricity?.total_electricity_due || 0)} due`,
      icon: Zap,
      color: 'warning',
      trend: stats?.electricity ? ((stats.electricity.total_electricity_collected / stats.electricity.total_electricity_due) * 100).toFixed(1) : 0
    },
    {
      title: 'Total Arrears',
      value: formatCurrency((stats?.arrears?.total_rent_arrears || 0) + (stats?.arrears?.total_electricity_arrears || 0)),
      subtitle: `${stats?.arrears?.rooms_with_arrears || 0} rooms affected`,
      icon: AlertTriangle,
      color: 'danger',
      trend: null
    }
  ];

  // Chart colors
  const COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981'];

  // Generate dynamic room colors (avoiding red shades)
  const generateRoomColors = (rooms) => {
    const colorPalette = [
      '#10b981', // Green
      '#3b82f6', // Blue
      '#8b5cf6', // Purple
      '#f59e0b', // Amber/Orange
      '#06b6d4', // Cyan
      '#14b8a6', // Teal
      '#6366f1', // Indigo
      '#ec4899', // Pink
      '#84cc16', // Lime-green
      '#0ea5e9', // Sky blue
      '#a855f7', // Violet
      '#22d3ee', // Cyan-light
    ];
    
    const roomColors = {};
    rooms.forEach((room, index) => {
      roomColors[room] = colorPalette[index % colorPalette.length];
    });
    
    return roomColors;
  };

  // Get all unique room numbers from electricity data
  const getAllRoomNumbers = () => {
    if (!stats?.electricity_trends) return [];
    
    const roomSet = new Set();
    const trends = stats.electricity_trends.six_months; // Use any period to get rooms
    
    trends.forEach(monthData => {
      if (monthData.room_breakdown) {
        monthData.room_breakdown.forEach(room => {
          roomSet.add(room.room_number);
        });
      }
    });
    
    return Array.from(roomSet).sort();
  };

  const allRooms = getAllRoomNumbers();
  const ROOM_COLORS = generateRoomColors(allRooms);

  // Get owner contribution color based on value
  const getOwnerContributionColor = (contribution) => {
    return contribution > 0 ? '#ef4444' : '#84cc16'; // Red if positive, Lime if zero/negative
  };

  // Get electricity trends based on selected period
  const getElectricityData = () => {
    if (!stats?.electricity_trends) return [];
    
    let rawData;
    switch(selectedPeriod) {
      case '6':
        rawData = stats.electricity_trends.six_months;
        break;
      case '12':
        rawData = stats.electricity_trends.twelve_months;
        break;
      case '24':
        rawData = stats.electricity_trends.twenty_four_months;
        break;
      default:
        rawData = stats.electricity_trends.six_months;
    }

    // Transform data for stacked bar chart
    return rawData.map(monthData => {
      const result = {
        month: monthData.month,
        total_electricity: monthData.total_electricity,
        owner_contribution: monthData.owner_contribution || 0,
        owner_color: getOwnerContributionColor(monthData.owner_contribution || 0)
      };

      // Add each room's contribution as a separate property
      if (monthData.room_breakdown) {
        monthData.room_breakdown.forEach(room => {
          // Filter rooms if specific room is selected
          if (selectedRooms === 'all' || selectedRooms === room.room_number) {
            result[room.room_number] = room.room_contribution;
          }
        });
      }

      return result;
    });
  };

  const electricityData = getElectricityData();

  // Calculate electricity growth
  const currentMonthElectricity = electricityData.length > 0 
    ? electricityData[electricityData.length - 1]?.total_electricity || 0 
    : 0;

  const previousMonthElectricity = electricityData.length > 1 
    ? electricityData[electricityData.length - 2]?.total_electricity || 0 
    : 0;

  const electricityGrowth = previousMonthElectricity > 0 
    ? ((currentMonthElectricity - previousMonthElectricity) / previousMonthElectricity * 100).toFixed(1)
    : 0;

  // Get electricity consumption data
  const electricityConsumption = stats?.electricity_consumption?.tenants || [];

  // Calculate payment status from room data instead of separate counts
  const totalRooms = stats?.rooms?.occupied_rooms || 0;
  const rentCollected = stats?.rent?.total_rent_collected || 0;
  const rentDue = stats?.rent?.total_rent_due || 0;
  
  // Calculate approximate payment status distribution
  const collectionRate = rentDue > 0 ? (rentCollected / rentDue) : 0;
  const paidCount = Math.round(totalRooms * collectionRate);
  const pendingCount = totalRooms - paidCount;
  
  const paymentStatusData = [
    { name: 'Rent Paid', value: paidCount || 0, color: '#10b981' },
    { name: 'Rent Pending', value: pendingCount || 0, color: '#ef4444' },
    { name: 'Rent Partial', value: 0, color: '#f59e0b' }
  ].filter(item => item.value > 0); // Only show categories with values

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Dashboard</h1>
          <p className="text-secondary-500 dark:text-secondary-400">Overview for {currentMonth}</p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-700 text-white text-sm font-semibold shadow-md hover:shadow-lg hover:from-indigo-700 hover:to-blue-800 transition-all"
        >
          <TrendingUp className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          {
            title: 'Total Rooms',
            value: stats?.rooms?.total_rooms || 0,
            subtitle: `${stats?.rooms?.occupied_rooms || 0} occupied`,
            icon: Building,
            gradient: 'from-emerald-500 to-teal-600',
            trend: null
          },
          {
            title: 'Total Tenants',
            value: stats?.tenants?.total_tenants || 0,
            subtitle: 'Active tenants',
            icon: Users,
            gradient: 'from-blue-500 to-indigo-600',
            trend: null
          },
          {
            title: 'Monthly Rent',
            value: formatCurrency(stats?.rent?.total_rent_collected || 0),
            subtitle: `of ${formatCurrency(stats?.rent?.total_rent_due || 0)} due`,
            icon: DollarSign,
            gradient: 'from-green-500 to-emerald-600',
            trend: stats?.rent?.total_rent_due > 0
              ? ((stats.rent.total_rent_collected / stats.rent.total_rent_due) * 100).toFixed(1)
              : null
          },
          {
            title: 'Electricity',
            value: formatCurrency(stats?.electricity?.total_electricity_collected || 0),
            subtitle: `of ${formatCurrency(stats?.electricity?.total_electricity_due || 0)} due`,
            icon: Zap,
            gradient: 'from-amber-400 to-orange-500',
            trend: stats?.electricity?.total_electricity_due > 0
              ? ((stats.electricity.total_electricity_collected / stats.electricity.total_electricity_due) * 100).toFixed(1)
              : null
          },
          {
            title: 'Total Arrears',
            value: formatCurrency((stats?.arrears?.total_rent_arrears || 0) + (stats?.arrears?.total_electricity_arrears || 0)),
            subtitle: `${stats?.arrears?.rooms_with_arrears || 0} rooms affected`,
            icon: AlertTriangle,
            gradient: 'from-red-600 to-rose-700',
            trend: null
          }
        ].map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.08 }}
            className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center shadow-md flex-shrink-0`}>
                <card.icon className="w-5 h-5 text-white" />
              </div>
              {card.trend && (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-900/30">
                  <TrendingUp className="w-3 h-3 text-green-600 dark:text-green-400" />
                  <span className="text-xs font-semibold text-green-600 dark:text-green-400">{card.trend}%</span>
                </div>
              )}
            </div>
            <p className="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1">{card.title}</p>
            <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100 leading-tight">{card.value}</p>
            <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">{card.subtitle}</p>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Electricity Bills Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden"
        >
          {/* Card gradient header */}
          <div className="px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-600 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Monthly Electricity Bills</h3>
              <p className="text-amber-100 text-xs">Room-wise breakdown & owner contribution</p>
            </div>
          </div>

          <div className="p-5">
            {/* Filter Controls */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Period</label>
                <select
                  className="form-input text-sm w-full"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                >
                  <option value="6">Last 6 Months</option>
                  <option value="12">Last 12 Months</option>
                  <option value="24">Last 24 Months</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Room</label>
                <select
                  className="form-input text-sm w-full"
                  value={selectedRooms}
                  onChange={(e) => setSelectedRooms(e.target.value)}
                >
                  <option value="all">All Rooms</option>
                  {allRooms.map(room => (
                    <option key={room} value={room}>{room}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">View</label>
                <select
                  className="form-input text-sm w-full"
                  value={chartViewType}
                  onChange={(e) => setChartViewType(e.target.value)}
                >
                  <option value="stacked">Stacked</option>
                  <option value="grouped">Grouped</option>
                </select>
              </div>
            </div>

            {/* Current month KPI */}
            <div className="flex items-center justify-between mb-4 p-3 bg-secondary-50 dark:bg-secondary-700/40 rounded-xl">
              <span className="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Current Month Total</span>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                  {formatCurrency(currentMonthElectricity)}
                </span>
                {electricityGrowth !== 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    electricityGrowth > 0
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                  }`}>
                    {electricityGrowth > 0 ? '+' : ''}{electricityGrowth}%
                  </span>
                )}
              </div>
            </div>

            {electricityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={electricityData}
                  margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === 'owner_contribution') return [formatCurrency(value), 'Owner Contribution'];
                      return [formatCurrency(value), name];
                    }}
                    labelFormatter={(label) => `Month: ${label}`}
                    contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '16px' }} iconType="circle" />
                  {(selectedRooms === 'all' ? allRooms : [selectedRooms]).map(room => (
                    <Bar
                      key={room}
                      dataKey={room}
                      stackId={chartViewType === 'stacked' ? 'electricity' : undefined}
                      fill={ROOM_COLORS[room]}
                      name={room}
                      radius={chartViewType === 'grouped' ? [4, 4, 0, 0] : 0}
                    />
                  ))}
                  <Bar
                    dataKey="owner_contribution"
                    stackId={chartViewType === 'stacked' ? 'electricity' : undefined}
                    name="Owner Contribution"
                    radius={[4, 4, 0, 0]}
                  >
                    {electricityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getOwnerContributionColor(entry.owner_contribution)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-3 opacity-40">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-secondary-500 dark:text-secondary-400 text-sm">No electricity bill data available</p>
                  <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-1">Trends will appear after bills are recorded</p>
                </div>
              </div>
            )}

            {/* Footer summary */}
            <div className="mt-4 pt-4 border-t border-secondary-100 dark:border-secondary-700">
              <div className="grid grid-cols-3 gap-4 text-center mb-3">
                <div>
                  <p className="text-sm font-bold text-secondary-900 dark:text-secondary-100">
                    {electricityData.reduce((sum, m) => sum + (m.bill_count || 0), 0)}
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">Total Bills</p>
                </div>
                <div>
                  <p className="text-sm font-bold text-secondary-900 dark:text-secondary-100">
                    {Math.round(electricityData.reduce((sum, m) => sum + (m.total_units || 0), 0))} kWh
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">Total Units</p>
                </div>
                <div>
                  <p className="text-sm font-bold text-secondary-900 dark:text-secondary-100">
                    {formatCurrency(electricityData.reduce((sum, m) => sum + (m.total_electricity || 0), 0))}
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">Total Cost</p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                  <span className="text-xs text-secondary-500 dark:text-secondary-400">Owner Pays (Deficit)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-lime-500"></div>
                  <span className="text-xs text-secondary-500 dark:text-secondary-400">Fully Covered / Surplus</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Electricity Consumption */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden"
        >
          {/* Card gradient header */}
          <div className="px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Electricity Consumption</h3>
                <p className="text-amber-100 text-xs">For {getSelectedMonthLabel()}</p>
              </div>
            </div>
            <select
              className="px-2 py-1 text-xs rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:ring-1 focus:ring-white/50"
              value={selectedConsumptionMonth}
              onChange={(e) => setSelectedConsumptionMonth(e.target.value)}
            >
              {monthOptions.map((option) => (
                <option key={option.value} value={option.value} className="text-secondary-900">
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="p-5">
            <div className="space-y-4">
              {electricityConsumption.slice(0, 5).map((tenant, index) => (
                <div key={index} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">{index + 1}</span>
                      </div>
                      <span className="font-medium text-secondary-900 dark:text-secondary-100 text-sm">{tenant.tenant_name}</span>
                      <span className="text-xs text-secondary-400 dark:text-secondary-500">({tenant.room_number})</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-secondary-900 dark:text-secondary-100">{tenant.units_consumed} kWh</div>
                      <div className="text-xs text-secondary-500 dark:text-secondary-400">{formatCurrency(tenant.cost)}</div>
                    </div>
                  </div>
                  <div className="w-full bg-secondary-100 dark:bg-secondary-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${getConsumptionBarColor(tenant.percentage)}`}
                      style={{ width: `${Math.max(tenant.percentage, 5)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t border-secondary-100 dark:border-secondary-700">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-secondary-50 dark:bg-secondary-700/40 rounded-xl">
                  <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                    {stats?.electricity_consumption?.total_units || 0} kWh
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">Total Consumption</p>
                </div>
                <div className="text-center p-3 bg-secondary-50 dark:bg-secondary-700/40 rounded-xl">
                  <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                    {formatCurrency(stats?.electricity_consumption?.total_cost || 0)}
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">Total Cost</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Recent Transactions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden"
      >
        <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-blue-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-white font-semibold text-sm">Recent Transactions</h3>
          </div>
          {stats?.recent_transactions?.length > 0 && (
            <span className="text-indigo-100 text-xs font-medium">{stats.recent_transactions.length} record{stats.recent_transactions.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {stats?.recent_transactions?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary-100 dark:border-secondary-700">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Room</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Tenant</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Method</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700">
                {stats.recent_transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-700/30 transition-colors">
                    <td className="px-6 py-3.5 text-sm text-secondary-600 dark:text-secondary-400">
                      {new Date(transaction.payment_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{transaction.room_number}</span>
                    </td>
                    <td className="px-6 py-3.5 text-sm text-secondary-700 dark:text-secondary-300">{transaction.tenant_name || 'N/A'}</td>
                    <td className="px-6 py-3.5">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        transaction.payment_type === 'rent'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                      }`}>
                        {transaction.payment_type}
                      </span>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(transaction.amount)}</span>
                    </td>
                    <td className="px-6 py-3.5 text-sm text-secondary-600 dark:text-secondary-400">{transaction.payment_method || 'Cash'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-14 px-6">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-700 flex items-center justify-center mx-auto mb-3 opacity-50">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <p className="text-secondary-500 dark:text-secondary-400 text-sm">No recent transactions</p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Dashboard;
